import DOMPurify from 'dompurify';
import { marked } from 'marked';

const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'div',
  'strong', 'b', 'em', 'i',
  'ul', 'ol', 'li',
  'a', 'blockquote',
];

const ALLOWED_ATTR = ['href', 'target', 'rel'];

const STRUCTURAL_TAG_RE = /<(h[1-6]|ul|ol|li|blockquote|strong|em|b|i|a)\b/i;

const MARKDOWN_PATTERNS = [
  /^#{1,6}\s/m,
  /^\s*[-*+]\s/m,
  /^\s*\d+\.\s/m,
  /^>\s/m,
  /\*\*[^*\n]+\*\*/,
  /(?<!\*)\*(?!\*)[^*\n]+\*(?!\*)/,
  /__[^_\n]+__/,
  /(?<!_)_(?!_)[^_\n]+_(?!_)/,
  /\[.+?\]\(.+?\)/,
];

marked.setOptions({ gfm: true, breaks: true });

export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
}

export function looksLikeMarkdown(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return false;
  return MARKDOWN_PATTERNS.some((re) => re.test(trimmed));
}

export function markdownToHtml(text) {
  return sanitizeHtml(marked.parse(text));
}

export function hasRichHtml(html) {
  if (!html) return false;
  const stripped = html.replace(/<!--[\s\S]*?-->/g, '').trim();
  return /<(p|h[1-6]|ul|ol|li|blockquote|strong|em|b|i|table)[\s>]/i.test(stripped);
}

export function convertPasteToHtml(html, plainText) {
  if (html && hasRichHtml(html)) {
    return sanitizeHtml(html);
  }
  if (plainText && looksLikeMarkdown(plainText)) {
    return markdownToHtml(plainText);
  }
  return null;
}

export function isHtmlContent(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  return STRUCTURAL_TAG_RE.test(trimmed) || /^<[a-z][^>]*>/i.test(trimmed);
}

export function stripHtmlToPlainText(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

export function normalizeEditorOutput(html) {
  const sanitized = sanitizeHtml(html);
  const plain = (new DOMParser().parseFromString(sanitized, 'text/html').body.textContent || '').trim();
  if (!plain) return '';

  if (!STRUCTURAL_TAG_RE.test(sanitized)) {
    return plain;
  }
  return sanitized.trim();
}

export function contentForEditor(value) {
  if (!value) return '';
  if (isHtmlContent(value)) return sanitizeHtml(value);
  return value.replace(/\n/g, '<br>');
}

export function isRichContentEmpty(value) {
  if (!value || !String(value).trim()) return true;
  const plain = isHtmlContent(value)
    ? (new DOMParser().parseFromString(sanitizeHtml(value), 'text/html').body.textContent || '')
    : String(value);
  return !plain.trim();
}
